import { useEffect } from "react";
import { useTourState } from "@heroncendre/player";
import { PARAM_SCENE } from "./UrlScene.js";

/**
 * ============================================================================
 *  RaccourciEdition.tsx — De la visite à l'éditeur, sur la scène courante
 * ----------------------------------------------------------------------------
 *  Quand on règle une visite, on la regarde, on voit ce qui cloche dans CETTE
 *  scène, et on veut la corriger là. Passer par `?edit` ramenait à la première
 *  scène : il fallait la retrouver dans l'arbre.
 *
 *  La touche `E` ouvre l'éditeur sur la scène affichée ; `Maj+E` en sort, dans
 *  l'éditeur (`EditorView`), vers la visite sur la même scène. Il n'y a plus de
 *  bouton « Éditer » : l'en-tête appartient au visiteur, et la touche suffit à
 *  qui construit la visite. L'adresse de l'éditeur est celle que la visite sait
 *  déjà lire (`?edit&scene=<id>`, voir `UrlScene.tsx`).
 *
 *  ── Pourquoi seulement en local ───────────────────────────────────────────
 *
 *  L'éditeur n'a de sens que face à un serveur de développement qui écrit le
 *  document. Une visite livrée ne doit offrir aucune porte vers lui : le
 *  raccourci ne s'arme donc que sur `localhost`, et jamais sur le site publié.
 * ==========================================================================*/

const HOTES_LOCAUX = new Set(["localhost", "127.0.0.1", "[::1]"]);

export const estLocal = (): boolean => HOTES_LOCAUX.has(window.location.hostname);

/** L'adresse de l'éditeur sur une scène. */
export const adresseEdition = (sceneId: string): string =>
  `?edit&${PARAM_SCENE}=${encodeURIComponent(sceneId)}`;

/** Une saisie en cours : la touche appartient au champ, pas au raccourci. */
export function dansUnChamp(cible: EventTarget | null): boolean {
  const element = cible as HTMLElement | null;
  const balise = element?.tagName;
  return (
    balise === "INPUT" || balise === "TEXTAREA" || balise === "SELECT" || !!element?.isContentEditable
  );
}

/** Arme la touche `E`. Ne rend rien. */
export function RaccourciEdition({ actif }: { actif: boolean }) {
  const sceneId = useTourState((state) => state.currentSceneId);

  useEffect(() => {
    if (!actif || !estLocal()) return;

    const surTouche = (event: KeyboardEvent) => {
      if (event.key.toLowerCase() !== "e") return;
      // Ctrl+E, Cmd+E, Alt+E sont à d'autres usages (barre d'adresse, menus).
      if (event.ctrlKey || event.metaKey || event.altKey || event.shiftKey) return;
      if (event.repeat || dansUnChamp(event.target)) return;
      event.preventDefault();
      window.location.assign(adresseEdition(sceneId));
    };

    window.addEventListener("keydown", surTouche);
    return () => window.removeEventListener("keydown", surTouche);
  }, [actif, sceneId]);

  return null;
}
